import { resolveScaleWebsocketUrl } from '../api/scaleApi';

const listeners = new Set();
let socket = null;
let currentScopeKey = null;
let connectionFactory = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let connectionGeneration = 0;

const clearReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const notifyListeners = (payload) => {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('Error in scale event listener:', error);
    }
  });
};

const closeSocket = () => {
  if (!socket) return;
  try {
    socket.close();
  } catch (error) {
    console.error('Error closing scale WebSocket:', error);
  }
  socket = null;
};

const scheduleReconnect = () => {
  clearReconnect();
  if (!currentScopeKey || !connectionFactory) return;
  const delay = Math.min(15000, 1000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    openSocket().catch((error) => {
      console.error('Error reconnecting scale WebSocket:', error);
    });
  }, delay);
};

const openSocket = async () => {
  const generation = connectionGeneration;
  if (!currentScopeKey || typeof connectionFactory !== 'function') return;
  clearReconnect();
  notifyListeners({
    type: 'scale.connection',
    connectionStatus: 'connecting',
    scopeKey: currentScopeKey,
  });

  let config;
  try {
    config = await connectionFactory();
  } catch (error) {
    if (generation !== connectionGeneration) return;
    notifyListeners({
      type: 'scale.connection',
      connectionStatus: 'error',
      scopeKey: currentScopeKey,
      errorMessage: error?.response?.data?.message || error?.message || 'No se pudo conectar a la balanza.',
    });
    scheduleReconnect();
    return;
  }

  if (generation !== connectionGeneration || !currentScopeKey) return;

  notifyListeners({
    type: 'scale.bootstrap',
    scopeKey: currentScopeKey,
    status: config?.status || 'unpaired',
    binding: config?.binding || null,
    registeredDevice: config?.registeredDevice || null,
    cashRegisterSessionId: config?.cashRegisterSessionId ?? null,
  });

  const websocketUrl = resolveScaleWebsocketUrl(config?.websocketUrl || '/ws-scale/consume', config?.token);
  const nextSocket = new WebSocket(websocketUrl);
  socket = nextSocket;

  nextSocket.onopen = () => {
    if (socket !== nextSocket) return;
    reconnectAttempts = 0;
    notifyListeners({
      type: 'scale.connection',
      connectionStatus: 'connected',
      scopeKey: currentScopeKey,
    });
  };

  nextSocket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);
      notifyListeners(payload);
    } catch (error) {
      console.error('Error parsing scale WebSocket payload:', error);
    }
  };

  nextSocket.onerror = () => {
    notifyListeners({
      type: 'scale.connection',
      connectionStatus: 'error',
      scopeKey: currentScopeKey,
      errorMessage: 'La conexion con la balanza se interrumpio.',
    });
  };

  nextSocket.onclose = () => {
    if (socket === nextSocket) {
      socket = null;
    }
    notifyListeners({
      type: 'scale.connection',
      connectionStatus: currentScopeKey ? 'disconnected' : 'idle',
      scopeKey: currentScopeKey,
    });
    if (currentScopeKey) {
      scheduleReconnect();
    }
  };
};

export const startScaleEvents = ({ scopeKey, getConnectionConfig } = {}) => {
  const normalizedScopeKey = String(scopeKey || '').trim();
  if (!normalizedScopeKey || typeof getConnectionConfig !== 'function') {
    stopScaleEvents();
    return;
  }

  connectionFactory = getConnectionConfig;
  if (currentScopeKey === normalizedScopeKey && socket) {
    return;
  }

  currentScopeKey = normalizedScopeKey;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  clearReconnect();
  closeSocket();
  openSocket().catch((error) => {
    console.error('Error opening scale WebSocket:', error);
  });
};

export const stopScaleEvents = () => {
  currentScopeKey = null;
  connectionFactory = null;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  clearReconnect();
  closeSocket();
  notifyListeners({
    type: 'scale.connection',
    connectionStatus: 'idle',
    scopeKey: null,
  });
};

export const subscribeScaleEvents = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
